import React from 'react';

const Dancers = () => {
  // Placeholder data - you can replace this with actual dancer information
  const dancers = [
    {
      id: 1,
      name: "Jay",
      bio: `Hrithik Roshan got me hooked early, and dance has never let me go. I've trained in hip-hop, performed in stage musicals, and taught Zumba and choreography out of a garage during COVID. As an AI professional, I built Formations, an award-winning iPad app for choreographers, and have helped dance studios grow enrollment using AI. To me, dance carries emotion and culture in ways words can't, and my dream is to see a short story I've written become a full stage musical. 🎶`,
      image: "/dancers/jay.jpg"
    },
    {
      id: 2,
      name: "Deepshikha",
      bio: `It started with copying moves off the TV as a kid and somehow turned into owning every party and family celebration without even trying. Through school and college I chased every stage I could find and fell in love with jazz, hip hop, contemporary, and salsa along the way. Being part of this team is everything, the people, the energy, and creating something together that feels fun, powerful, and completely unforgettable.`,
      image: "/dancers/deepshikha.jpg"
    },
    {
      id: 3,
      name: "Asha",
      bio: `Dance found me early through Bharatanatyam, and even when life pulled me away, it quietly waited. Returning to Bollywood classes years later felt like coming home. Since then I've trained with Nach, Sun Bollywood, Max Dance Company, RAPA, Dance Identity, and performed with Aarat by Deepali Vichare. My range spans Bharatanatyam to Bollywood, folk, freestyle, and hip-hop. But more than the styles or stages, dance is how I stay connected to something I love and the people I find along the way.`,
      image: "/dancers/asha.jpg"
    },
    {
      id: 4,
      name: "Akanksha",
      bio: `My mom signing me up for Kathak and Bollywood classes as a kid turned out to be one of the best things she ever did for me. Through college competitions and beyond, dance has quietly shaped who I am. I love Bollywood fusion, freestyle, classical styles, and folk dances like Lavni, Ghoomar, and Bhangra that showed me how rich and diverse Indian dance really is. I'm a little shy at first, but dance is where I open up, find my confidence, and feel most like myself. Let's Nacho!!`,
      image: "/dancers/akanksha.jpg"
    },
    {
      id: 5,
      name: "Vishal",
      bio: `Before tech, before the US, there was dance. Growing up in India, Bollywood was everywhere and I was always the one pulling cousins into formations at weddings and festivals. That joy carried me to a state-level championship with my college team. Then life got busy and the music quietly stopped. A friend dragged me back into a studio one day and something clicked back into place. That friend is now my wife. I helped found Offbeat Crew because that feeling of finding your rhythm again? Everyone deserves it.`,
      image: "/dancers/vishal.jpg"
    },
    {
      id: 6,
      name: "Rushil",
      bio: `Rhythm found me in my school days in India and never really let go. Since then I've moved through Bolly Hop, hip-hop, shuffle, and salsa, each style teaching me something new about expression and freedom. By day I write code and live in logic, but when the music hits, all of that falls away. Dance is not just something I do. It is how I feel alive.`,
      image: "/dancers/rushil.jpg"
    },
    {
      id: 7,
      name: "Dheepak",
      bio: "Some of my favorite memories start with a five year old dancing at an amusement park, completely free. That child is still in every rehearsal, every performance, every quiet moment when the music takes over. Dance has always been how I speak when words are not enough, and over time it became something I genuinely cannot live without. Being part of Offbeat goes beyond steps and stages for me. It is about community, shared rhythm, and creating something that moves people. Cannot wait to share that with you all.",
      image: "/dancers/dheepak.jpg"
    }
  ];

  // Shuffle dancers array on component mount
  const shuffledDancers = [...dancers].sort(() => Math.random() - 0.5);

  return (
    <section id="dancers" className="py-20 bg-light-gold">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-text-dark mb-4">
            Meet Our <span 
              style={{
                background: 'linear-gradient(90deg, #FC9C24, #E454B4, #5454B4, #0C9CCC)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >Dancers</span>
          </h2>
          <p className="text-xl text-text-dark max-w-3xl mx-auto opacity-88">
            Talented individuals coming together to create magic through movement
          </p>
          <div className="w-24 h-1 mx-auto mt-4" style={{
            background: 'linear-gradient(90deg, #FC9C24, #E454B4, #5454B4, #0C9CCC)'
          }}></div>
        </div>

        <div className="space-y-8 md:space-y-12">
          {shuffledDancers.map((dancer) => (
            <div
              key={dancer.id}
              className="bg-white rounded-xl md:rounded-2xl shadow-lg overflow-hidden card-hover border border-orange-gold/30"
            >
              <div className="flex flex-col md:flex-row">
                <div className="w-full md:w-2/5 lg:w-1/3">
                  <div className="aspect-square bg-gradient-to-br from-warm-yellow/30 to-yellow-soft/30 flex items-center justify-center overflow-hidden">
                    <img 
                      src={dancer.image} 
                      alt={dancer.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        // Fallback to emoji if image fails to load
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        target.nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                    <div className="text-6xl md:text-8xl text-orange-gold/40 hidden">
                      🕺
                    </div>
                  </div>
                </div>
                
                <div className="w-full md:w-3/5 lg:w-2/3 p-6 md:p-8">
                  <div className="mb-4">
                    <h3 className="text-xl md:text-2xl font-bold text-text-dark mb-2">{dancer.name}</h3>
                  </div>
                  
                  <div className="prose prose-gray max-w-none">
                    <div className="relative">
                      <svg className="absolute -top-2 -left-2 w-8 h-8 text-orange-gold/30" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                      </svg>
                      <p className="text-text-dark text-sm md:text-base leading-relaxed whitespace-pre-line italic pl-6 border-l-4 border-orange-gold/50 bg-warm-yellow/20 p-4 rounded-r-lg">
                        {dancer.bio}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 text-center">
          </div>
      </div>
    </section>
  );
};

export default Dancers;
